import { ErrorHandler, inject, Injectable, signal } from '@angular/core';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { defaultIfEmpty, delay, finalize, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';
import { SsApiService } from './api/ss-api.service';
import { UserService } from './user/user.service';
import { TimeService } from './time.service';
import { StaticContentService } from './static-content.service';
import { CurrencyService } from './currency.service';
import { PlatformService } from 'ngx-unificator/services';

interface TicketPrice {
  price: number;
  ticket: string;
  currencySymbol?: string;
}

export const DEP_AMOUNT_STR = 'depAmount';

@Injectable({
  providedIn: 'root',
})
export class LotteryService {
  private _api = inject(SsApiService);
  private _user = inject(UserService);
  private _currency = inject(CurrencyService);
  private _time = inject(TimeService);
  private _static = inject(StaticContentService);
  private _platform = inject(PlatformService);
  private _errorHandler = inject(ErrorHandler);

  /**
   * Today current time
   * @private
   */
  private _todayTime = this._time.toLocalDate().setHours(0, 0, 0, 0);
  /**
   * Current lottery data
   * @private
   */
  private _currentLottery: any = null;
  private _selectingLotteryId: number | string | null = null;
  /**
   * Current lottery data
   * @private
   */
  private _currentLottery$: ReplaySubject<any> = new ReplaySubject<any>();
  /**
   * Current lottery data by player
   * @private
   */
  private _playerLottery: any = null;
  /**
   * User player lottery tickets
   * @private
   */
  private _usedTickets: number = 0;
  /**
   * Plaer ticket status
   */
  public updateLotteryStatus$: ReplaySubject<any> = new ReplaySubject(1);
  public countUserTickets$: Observable<number> = this.checkLotteryCountTickets$();
  private _ticketRates: TicketPrice[] = [];
  /**
   * Show lottery link
   */
  public showLotteryLink = false;

  public playerTickets = signal(0);

  public get ticketRates(): any[] {
    return this._ticketRates;
  }
  public get currentLottery() {
    return this._currentLottery;
  }
  public get currentLottery$() {
    return this._currentLottery$;
  }
  /**
   * Access to used tickets
   */
  public get usedTickets() {
    return this._usedTickets;
  }
  public get lotteryAvailableTickets() {
    return this._playerLottery?.max_tickets_count_to_player;
  }
  /**
   * Set used tickets
   * @param value
   */
  public set usedTickets(value: number) {
    this._usedTickets = value;
  }

  /**
   * Stags item lottery
   * @private
   */
  private _isLotteryAvailableForStag() {
    return this._static.item({ slug: 'lottery-stag' });
  }
  /**
   * Check current lottery by today time
   * @param lotteryList
   * @private
   */
  private _isCheckCurrentLottery$(lotteryList: any) {
    if (!Array.isArray(lotteryList)) {
      return of(null);
    }

    const lottery = lotteryList.find((lottery) => {
      const hasStarted = this._time.toLocalDate(lottery?.start_at).setHours(0, 0, 0, 0) <= this._todayTime;
      const hasEnded = this._time.toLocalDate(lottery?.end_at).setHours(0, 0, 0, 0) <= this._todayTime;
      return (hasStarted && !hasEnded && !this._user.info.currency) || Object.keys(lottery?.deposit_scale_rules).includes(this._user.info.currency);
    });

    if (!lottery) {
      return of(null);
    }

    this._generateTicketRates(this._getTicketPrice(lottery), lottery?.max_tickets_count_to_player, lottery);
    return of(lottery);
  }
  /**
   * Get current lottery by today date
   */
  public getCurrentLottery$(): Observable<any> {
    return this._api.getLotteries().pipe(
      delay(100),
      switchMap((lotteryList: any) => this._isCheckCurrentLottery$(lotteryList)),
      map((lottery) => {
        if (lottery) {
          this._currentLottery = {
            ...lottery,
            end_at: this._time.toLocalDate(lottery?.end_at),
          };
          this._generateTicketRates(this._getTicketPrice(this._currentLottery), lottery?.max_tickets_count_to_player);
        }

        return this._currentLottery;
      }),
      shareReplay(1),
    );
  }
  /**
   * Set amount tickets by deposit amount value
   * @param amount
   * @param lottery
   */
  public setDepositAmount(amount: number, lottery?: any) {
    this.checkLotteryCountTickets$()
      .pipe(
        tap(() => {
          if (!this._currentLottery && !lottery) return;
          const currencyName = this._user.info.currency;
          const lotteryCurrencyInfo = this.getLotteryCurrencyInfo(currencyName, lottery);
          const step = this._currency.subunitsToUnits(lotteryCurrencyInfo[0].price_cents, currencyName);
          const sumTicket = this.usedTickets + Math.floor(amount / step);
          this.playerTickets.set(
            sumTicket > (lottery || this._currentLottery).max_tickets_count_to_player
              ? (lottery || this._currentLottery).max_tickets_count_to_player
              : sumTicket,
          );
        }),
      )
      .subscribe();
  }
  public getLotteryCurrencyInfo(currency, lottery?) {
    return (lottery || this._currentLottery)?.deposit_scale_rules?.[currency];
  }
  /**
   * Activate lottery
   */
  public selectLottery() {
    const lotteryId = this._currentLottery?.id;
    if (!lotteryId) return;

    if (this._isSameLottery(this._selectedLotteryId, lotteryId)) {
      this.updateLotteryStatus$.next(lotteryId);
      return;
    }

    if (this._isSameLottery(this._selectingLotteryId, lotteryId)) return;

    this._selectingLotteryId = lotteryId;
    this._api
      .toggleSelectLottery(this._user.info.currency, { selected_lottery: lotteryId, currency: this._user.info.currency })
      .pipe(finalize(() => (this._selectingLotteryId = null)))
      .subscribe({
        next: () => {
          this._setSelectedLotteryId(lotteryId);
          this.updateLotteryStatus$.next(lotteryId);
        },
        error: (error) => this._errorHandler.handleError(error),
      });
  }
  /**
   * Deactivate lottery
   */
  public unSelectLottery() {
    if (this._selectedLotteryId === null) {
      this.updateLotteryStatus$.next(null);
      return;
    }

    this._api.toggleSelectLottery(this._user.info.currency, { selected_lottery: null, currency: this._user.info.currency }).subscribe({
      next: () => {
        this._setSelectedLotteryId(null);
        this.updateLotteryStatus$.next(null);
      },
      error: (error) => this._errorHandler.handleError(error),
    });
  }
  /**
   * Check lottery count tickets
   */
  public checkLotteryCountTickets$(): Observable<any> {
    return this._isLotteryAvailableForStag().pipe(
      switchMap((response) => (response?.length ? this.getCurrentLottery$() : EMPTY)),
      switchMap((currentLottery: any) => (currentLottery ? this._api.getLotteriesIdStatus(currentLottery?.id) : EMPTY)),
      map((lottery) => {
        this.usedTickets = lottery?.tickets?.length ?? 0;
        this.selectLottery();
        return this.usedTickets;
      }),
      map((usedTickets: number) => {
        const arr = [];
        for (let i = 0; i < usedTickets; i++) {
          arr.push(i);
        }
        return arr;
      }),
      defaultIfEmpty(null),
      share(),
    );
  }
  /**
   * Check side menu by active lottery
   */
  public checkLotteryLinkVisibility$(): Observable<any> {
    return this._user.auth$.pipe(
      switchMap((auth) => {
        if (!auth) {
          return of(true);
        }

        return this._isLotteryAvailableForStag().pipe(
          switchMap((response) => (response?.length ? this._api.getLotteries() : EMPTY)),
          switchMap((lotteryList: any) => this._isCheckCurrentLottery$(lotteryList)),
          tap((data) => this._currentLottery$.next(data)),
        );
      }),
      shareReplay(1),
    );
  }
  public resolveShowLottery() {
    if (!this._platform.isBrowser) return;
    this.checkLotteryLinkVisibility$()
      .pipe(tap((data) => (this.showLotteryLink = !!data)))
      .subscribe();
  }
  private _generateTicketRates(priceForOneTicket: number, countTickets: number, lottery?) {
    if (priceForOneTicket) {
      this._ticketRates = [];

      for (let i = 1; i <= countTickets; i++) {
        this._ticketRates.push({
          price: priceForOneTicket * i,
          ticket: i === 1 ? 't.ticket' : 't.tickets',
          currencySymbol: this._currency.currencySymbol((lottery || this._currentLottery)?.currency),
        });
      }
    } else {
      this._ticketRates = [];
    }
  }
  private _getTicketPrice(lottery): number {
    const currencyName = this._user.info.currency;
    const lotteryCurrencyInfo = this.getLotteryCurrencyInfo(currencyName, lottery);
    return lotteryCurrencyInfo ? this._currency.subunitsToUnits(lotteryCurrencyInfo[0].price_cents, currencyName) : null;
  }

  private get _selectedLotteryId(): number | string | null {
    const selectedLotteryId = this._user.currentCurrency?.selected_lottery_id;
    return selectedLotteryId === undefined ? this._user.info.lottery ?? null : selectedLotteryId;
  }

  private _setSelectedLotteryId(lotteryId: number | string | null): void {
    this._user.info.lottery = lotteryId;
    this._user.currentCurrency.selected_lottery_id = lotteryId;
  }

  private _isSameLottery(firstId: number | string | null, secondId: number | string | null): boolean {
    return firstId !== null && secondId !== null && String(firstId) === String(secondId);
  }
}
